import { useState } from "react";
import { Box, Button, CircularProgress, TextField, Typography } from "@mui/material";
import { GovReport, acknowledgeReport } from "../../../../utils/icGovernance";
import ActionError from "../detail/ActionError";
import FieldGrid, { Field } from "../detail/FieldGrid";
import SideCard from "../detail/SideCard";
import { GovSettingsOrNull } from "../detail/types";
import { AMBER_TEXT, FAINT, primaryButtonSx } from "../detail/ui";
import { useGovAction } from "../detail/useGovAction";
import { clientLabel, dateTime } from "../governanceStatus";

/**
 * "{client} Acknowledged By / Date" section, with the Acknowledge form when
 * `permissions.acknowledge`.
 */
const AcknowledgeCard = ({
  report,
  settings,
  onAcknowledged,
  onReload,
}: {
  report: GovReport;
  settings: GovSettingsOrNull;
  onAcknowledged: (report: GovReport) => void;
  onReload: () => void;
}) => {
  const client = clientLabel(settings);
  const [comment, setComment] = useState("");
  const action = useGovAction();
  const ack = report.acknowledgement ?? { by: null, at: null, comment: null };
  const awaiting = report.status === "PUBLISHED" && report.acknowledgementRequired && !ack.by;

  const submit = async () => {
    const res = await action.run(() => acknowledgeReport(report.id, comment.trim() || undefined));
    if (res) {
      setComment("");
      onAcknowledged(res);
    }
  };

  const fields: Field[] = [
    {
      label: `${client} Acknowledged By`,
      value: ack.by ? (
        ack.by
      ) : awaiting ? (
        <Box component="span" sx={{ color: AMBER_TEXT, fontWeight: 700 }}>
          Awaiting acknowledgement
        </Box>
      ) : null,
    },
    { label: "Acknowledgement Date", value: ack.at ? dateTime(ack.at) : null },
  ];
  if (ack.comment) fields.push({ label: "Comment", value: ack.comment, full: true });

  return (
    <SideCard title={`${client} Acknowledged By / Date`}>
      <FieldGrid fields={fields} />
      {report.permissions?.acknowledge ? (
        <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid #f0f0f3" }}>
          <TextField
            label="Comment (optional)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            multiline
            minRows={2}
            fullWidth
            size="small"
            disabled={action.busy}
          />
          <Box sx={{ mt: 1.5 }}>
            <Button
              variant="contained"
              disableElevation
              onClick={submit}
              disabled={action.busy}
              startIcon={action.busy ? <CircularProgress size={16} color="inherit" /> : null}
              sx={primaryButtonSx}
            >
              Acknowledge report
            </Button>
          </Box>
          <Typography variant="caption" component="p" sx={{ color: FAINT, mt: 1 }}>
            Acknowledging confirms you have read this report. It does not approve anything in it.
          </Typography>
          <ActionError action={action} onReload={onReload} />
        </Box>
      ) : null}
    </SideCard>
  );
};

export default AcknowledgeCard;
